import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  fetchLibraryAppointments,
  deleteLibraryAppointment,
  getLibraryAppointmentSettings,
  updateLibraryAppointmentSettings,
  createLibraryAppointment,
  updateLibraryAppointment,
  staffConfirmLibraryAppointment,
  getLibraries,
  previewLibraryAppointmentExtension,
  extendLibraryAppointments,
  previewBulkCompleteLibraryAppointments,
  bulkCompleteLibraryAppointments
} from '../../services/libraryAppointmentService';
import { getUser, getLibraryId, isLibraryOnlyUser } from '../../services/authService';
import { TimePicker, message, Modal as AntdModal } from 'antd';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import './LibraryAppointments.scss';

dayjs.extend(customParseFormat);

const defaultFilters = {
  startDate: '',
  endDate: '',
  search: '',
  status: '',
  slotStartTime: '',
  libraryId: '',
  page: 1,
  pageSize: 20,
};

const STATUS_LABELS = {
  Pending: 'Beklemede',
  Confirmed: 'Kullanıcı Onayladı',
  StaffConfirmed: 'Personel Onayladı',
  Completed: 'Tamamlandı',
  Cancelled: 'İptal Edildi',
  Expired: 'Süresi Doldu',
};

const statusOptions = [
  { value: '', label: 'Tümü' },
  { value: 'Pending', label: STATUS_LABELS.Pending },
  { value: 'Confirmed', label: STATUS_LABELS.Confirmed },
  { value: 'StaffConfirmed', label: STATUS_LABELS.StaffConfirmed },
  { value: 'Completed', label: STATUS_LABELS.Completed },
  { value: 'Cancelled', label: STATUS_LABELS.Cancelled },
  { value: 'Expired', label: STATUS_LABELS.Expired },
];

// const TOTAL_SEATS limit removed, using settings.maxSeatsPerDay dynamically
// const ROW_COUNT removed; dynamically calculated based on maxSeatsPerDay
const DEFAULT_SEATS_PER_ROW = 15;
const DEFAULT_SEAT_PAGE_SIZE = 200;
const SEAT_CELL_MIN_WIDTH = 75; // min-width from SCSS: min-width: 75px
const SEAT_WIDTH = 65; // width from SCSS: width: 65px
const SEAT_CELL_PADDING = 12; // padding 6px * 2 = 12px
const SEAT_CELL_TOTAL_WIDTH = SEAT_CELL_MIN_WIDTH; // Her hücre minimum 75px
const LIBRARY_SEAT_MANAGER_EMAILS = ['kutuphane@iskenderun.bel.tr'];

const getTodayInputValue = () => {
  const now = new Date();
  now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
  return now.toISOString().split('T')[0];
};

const parseTimeToMinutes = (value = '00:00') => {
  const [hour = '0', minute = '0'] = value.split(':');
  const h = Number(hour);
  const m = Number(minute);
  if (Number.isNaN(h) || Number.isNaN(m)) return 0;
  return h * 60 + m;
};

const formatMinutesToTime = (minutes) => {
  const safe = Math.max(0, minutes);
  const hour = Math.floor(safe / 60);
  const minute = safe % 60;
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
};

const formatDateTime = (value, options) => {
  if (!value) return '';

  // Backend'den artık Istanbul saati geliyor (timezone offset olmadan)
  // Direkt parse et ve formatla
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return '';

  // timeZone belirtilmişse onu kullan, yoksa direkt formatla (zaten Istanbul saati)
  const formatOptions = options || {};

  return date.toLocaleString('tr-TR', formatOptions);
};

const normalizeSettings = (value = {}) => ({
  maxSeatsPerDay: value.maxSeatsPerDay ?? 95,
  minDaysBetweenBookings: value.minDaysBetweenBookings ?? 15,
  verificationCodeExpiryMinutes: value.verificationCodeExpiryMinutes ?? 20,
  workingDayStartTime: value.workingDayStartTime || '08:00',
  workingDayEndTime: value.workingDayEndTime || '18:00',
  slotDurationMinutes: value.slotDurationMinutes ?? 240,
  qrModeEnabled: value.qrModeEnabled ?? true,
});

const generateRandomCode = () => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let code = '';
  for (let i = 0; i < 16; i++) {
    if (i > 0 && i % 4 === 0) {
      code += '-';
    }
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
};

const LibraryAppointments = () => {
  const [filters, setFilters] = useState(defaultFilters);
  const [appointments, setAppointments] = useState([]);
  const [pagination, setPagination] = useState({ totalCount: 0, pageNumber: 1, pageSize: 20 });
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [libraries, setLibraries] = useState([]);
  const [librariesLoading, setLibrariesLoading] = useState(false);
  const loadAppointmentsRef = useRef(() => { });
  const [seatAppointments, setSeatAppointments] = useState({});
  const [seatLoading, setSeatLoading] = useState(false);
  const [seatError, setSeatError] = useState('');
  const [seatDate, setSeatDate] = useState(getTodayInputValue());
  const [selectedSeat, setSelectedSeat] = useState(null);
  const [isSeatModalOpen, setSeatModalOpen] = useState(false);
  const [editingSlot, setEditingSlot] = useState(null);
  const [seatsPerRow, setSeatsPerRow] = useState(DEFAULT_SEATS_PER_ROW);
  const seatTableWrapperRef = useRef(null);
  const [appointmentForm, setAppointmentForm] = useState({
    fullName: '',
    identityNumber: '',
    email: '',
    phone: '',
    publicCode: '',
  });
  const [formLoading, setFormLoading] = useState(false);
  const [confirmingAppointmentId, setConfirmingAppointmentId] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const lastProcessingTimeRef = useRef(0);
  const modalContainer = useMemo(() => (typeof document !== 'undefined' ? document.body : null), []);
  const searchDebounceRef = useRef(null);
  const searchInitializedRef = useRef(false);
  const [currentTime, setCurrentTime] = useState(() => new Date());
  const seatDateInputRef = useRef(null);

  const user = useMemo(() => getUser(), []);
  const navigate = useNavigate();
  const role = (user?.userType || user?.role || 'admin').toLowerCase();
  const isAdmin = role === 'admin';
  const normalizedEmail = (user?.email || '').toLowerCase();
  const canManageSeatAppointments =
    isAdmin || role === 'library' || LIBRARY_SEAT_MANAGER_EMAILS.includes(normalizedEmail);

  const slotOptions = useMemo(() => {
    if (
      !settings?.workingDayStartTime ||
      !settings?.workingDayEndTime ||
      !settings?.slotDurationMinutes
    ) {
      return [];
    }

    const startMinutes = parseTimeToMinutes(settings.workingDayStartTime);
    let endMinutes = parseTimeToMinutes(settings.workingDayEndTime);
    const duration = Number(settings.slotDurationMinutes) || 240;

    if (duration <= 0 || endMinutes <= startMinutes) {
      return [];
    }

    // 23:59'u 24:00 (1440 dakika) olarak ele al
    // Çünkü 23:59 genellikle günün sonunu temsil eder
    if (endMinutes === 1439) {
      endMinutes = 1440;
    }

    const options = [];
    let minutes = startMinutes;
    while (minutes < endMinutes) {
      const startLabel = formatMinutesToTime(minutes);

      let nextLimit = minutes + duration;
      let endTimeMinutes;
      let isLast = false;

      // If remaining time after this slot is less than a full duration, merge it
      if (endMinutes - nextLimit < duration) {
        endTimeMinutes = endMinutes;
        isLast = true;
      } else {
        endTimeMinutes = nextLimit > endMinutes ? endMinutes : nextLimit;
      }

      // 23:59 handling
      let displayEndMinutes = endTimeMinutes;
      if (displayEndMinutes >= 1440) {
        displayEndMinutes = 1439;
      }

      const endLabel = formatMinutesToTime(displayEndMinutes);
      options.push({
        value: startLabel,
        label: `${startLabel} - ${endLabel}`,
      });

      if (isLast) break;
      minutes = nextLimit;
    }

    return options;
  }, [settings]);

  useEffect(() => {
    if (!slotOptions.length) {
      return;
    }

    if (filters.slotStartTime == null) {
      setFilters((prev) => ({ ...prev, slotStartTime: slotOptions[0].value }));
      return;
    }

    if (
      filters.slotStartTime &&
      !slotOptions.some((option) => option.value === filters.slotStartTime)
    ) {
      setFilters((prev) => ({ ...prev, slotStartTime: slotOptions[0].value }));
    }
  }, [slotOptions, filters.slotStartTime]);

  const seatDisplaySlots = useMemo(() => slotOptions, [slotOptions]);
  const [selectedSlotValue, setSelectedSlotValue] = useState(null);

  // Auto-select slot based on current time
  useEffect(() => {
    if (seatDisplaySlots.length > 0) {
      // If user hasn't selected a slot yet, or date changes
      if (!selectedSlotValue) {
        // Check if seatDate is today
        const todayStr = new Date().toISOString().split('T')[0];
        if (seatDate === todayStr) {
          const now = new Date();
          const currentMinutes = now.getHours() * 60 + now.getMinutes();

          // Find the slot that contains current time
          // slotOptions format: value="08:00", label="08:00 - 13:00"
          const currentSlot = seatDisplaySlots.find((slot, index) => {
            const start = parseTimeToMinutes(slot.value);
            // Find end time of this slot (either next slot start or end of day)
            const nextSlot = seatDisplaySlots[index + 1];
            const end = nextSlot ? parseTimeToMinutes(nextSlot.value) : 1440; // 24:00

            return currentMinutes >= start && currentMinutes < end;
          });

          if (currentSlot) {
            setSelectedSlotValue(currentSlot.value);
            return;
          }
        }

        // Default to first slot if not today or no match
        setSelectedSlotValue(seatDisplaySlots[0].value);
      }
    }
  }, [seatDisplaySlots, selectedSlotValue, seatDate]);
  const seatDisplayValues = useMemo(
    () => seatDisplaySlots.map((option) => option.value),
    [seatDisplaySlots]
  );
  const selectedSlotIndex = useMemo(
    () => seatDisplaySlots.findIndex((option) => option.value === selectedSlotValue),
    [seatDisplaySlots, selectedSlotValue]
  );
  const nextSlotOption = selectedSlotIndex >= 0 ? seatDisplaySlots[selectedSlotIndex + 1] : null;

  const totalPages = useMemo(() => {
    if (!pagination?.totalCount || !pagination?.pageSize) return 1;
    return Math.ceil(pagination.totalCount / pagination.pageSize);
  }, [pagination]);

  // Dinamik satır başına masa sayısını hesapla
  useEffect(() => {
    const calculateSeatsPerRow = () => {
      // Ref ile wrapper'ı kontrol et
      const wrapper = seatTableWrapperRef.current;
      if (!wrapper) {
        return;
      }

      // Parent container'ı bul (.library-appointments veya .seat-section)
      const parentContainer = wrapper.closest('.seat-section') || wrapper.closest('.library-appointments');
      if (!parentContainer) {
        return;
      }

      // Parent container'ın genişliğini kullan (bu gerçek görünen genişlik)
      const parentWidth = parentContainer.clientWidth;
      if (parentWidth === 0) {
        return; // Henüz render edilmemiş
      }

      // Wrapper'ın genişliğini de kontrol et
      const wrapperWidth = wrapper.clientWidth;

      // Parent genişliğini kullan (wrapper genişliği table içeriğine göre değişebilir)
      // Ancak wrapper genişliği parent'tan küçükse (örneğin padding nedeniyle), wrapper'ı kullan
      const effectiveWidth = wrapperWidth > 0 && wrapperWidth < parentWidth ? wrapperWidth : parentWidth;

      // Scrollbar genişliğini hesapla (eğer scrollbar varsa)
      const hasScrollbar = wrapper.scrollWidth > wrapper.clientWidth;
      const scrollbarWidth = hasScrollbar ? 17 : 0; // Standart scrollbar genişliği

      // Her masa hücresi için minimum genişlik
      // Daha konservatif hesaplama: padding, scrollbar ve güvenlik payı
      // Library-appointments padding: 24px her iki tarafta = 48px
      // Ekstra güvenlik payı: 30px (zoom, rounding hataları ve border için)
      const availableWidth = effectiveWidth - 48 - scrollbarWidth - 30; // Padding + scrollbar + güvenlik payı
      let calculatedSeats = Math.floor(availableWidth / SEAT_CELL_TOTAL_WIDTH);

      // Eğer scrollbar varsa, bir masa daha az göster (daha güvenli)
      if (hasScrollbar && calculatedSeats > 0) {
        calculatedSeats = calculatedSeats - 1;
      }

      console.log('Parent width:', parentWidth, 'Wrapper width:', wrapperWidth, 'Has scrollbar:', hasScrollbar, 'Scrollbar width:', scrollbarWidth, 'Effective width:', effectiveWidth, 'Available:', availableWidth, 'Calculated seats:', calculatedSeats);

      // Minimum 5 masa, maksimum sınır yok (veya çok yüksek - 50)
      // Ancak hesaplama zaten konservatif olduğu için fazla çıkmamalı
      const seats = Math.max(5, Math.min(50, calculatedSeats || DEFAULT_SEATS_PER_ROW));

      setSeatsPerRow((prevSeats) => {
        if (prevSeats !== seats) {
          console.log('Seats per row changed from', prevSeats, 'to', seats);
          return seats;
        }
        return prevSeats;
      });
    };

    // İlk hesaplama için kısa bir gecikme (DOM hazır olana kadar)
    const timeoutId = setTimeout(() => {
      calculateSeatsPerRow();
    }, 200);

    // Resize event listener (debounce ile)
    let resizeTimeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        calculateSeatsPerRow();
      }, 150);
    };

    window.addEventListener('resize', handleResize);

    // ResizeObserver ile wrapper genişliği değişikliklerini izle
    const wrapper = seatTableWrapperRef.current;
    let resizeObserver;
    if (wrapper && window.ResizeObserver) {
      resizeObserver = new ResizeObserver(() => {
        calculateSeatsPerRow();
      });
      resizeObserver.observe(wrapper);
    }

    return () => {
      clearTimeout(timeoutId);
      clearTimeout(resizeTimeout);
      window.removeEventListener('resize', handleResize);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, []);

  const seatRows = useMemo(() => {
    const rows = [];
    let seatNumber = 1;
    const totalSeats = settings?.maxSeatsPerDay || 95;
    const rowCount = Math.max(1, Math.ceil(totalSeats / seatsPerRow));

    for (let rowIndex = 0; rowIndex < rowCount; rowIndex += 1) {
      const rowSeats = [];
      for (let colIndex = 0; colIndex < seatsPerRow; colIndex += 1) {
        if (seatNumber <= totalSeats) {
          rowSeats.push(seatNumber);
          seatNumber += 1;
        } else {
          rowSeats.push(null);
        }
      }
      rows.push(rowSeats);
    }

    return rows;
  }, [seatsPerRow, settings?.maxSeatsPerDay]);

  const seatInfoMap = useMemo(() => seatAppointments, [seatAppointments]);

  const getStatusModifier = (status) => {
    switch (status) {
      case 'StaffConfirmed':
        return 'status-staff';
      case 'Confirmed':
        return 'status-user';
      case 'Pending':
        return 'status-pending';
      case 'Cancelled':
      case 'Expired':
        return 'status-inactive';
      case 'Completed':
        // Tamamlanan randevular boş masa olarak gösterilir
        return 'status-available';
      default:
        return 'status-available';
    }
  };

  const getStatusText = (status) => {
    if (!status) {
      return 'Müsait';
    }

    return STATUS_LABELS[status] || status;
  };

  const handleSeatClick = (seatNumber) => {
    if (!selectedSlotValue) return;

    // Eğer modal zaten açıksa ve aynı masaya tıklanıyorsa, modal'ı kapat
    if (isSeatModalOpen && selectedSeat?.seatNumber === seatNumber) {
      handleCloseSeatModal();
      return;
    }

    const slots = seatInfoMap[seatNumber] || {};
    // Sadece seçili slot'u göster
    const filteredSlots = {};
    if (slots[selectedSlotValue]) {
      filteredSlots[selectedSlotValue] = slots[selectedSlotValue];
    }
    setSelectedSeat({ seatNumber, slots: filteredSlots });
    setSeatModalOpen(true);
  };

  const handleCloseSeatModal = () => {
    setSeatModalOpen(false);
    setSelectedSeat(null);
    setEditingSlot(null);
    setAppointmentForm({
      fullName: '',
      identityNumber: '',
      email: '',
      phone: '',
      publicCode: '',
    });
  };

  const handleEditSlot = (slotValue, slotInfo) => {
    // Eğer slotInfo varsa ama statüsü Completed, Cancelled veya Expired ise, yeni randevu ekleme moduna geç
    const isActiveAppointment = slotInfo &&
      slotInfo.status &&
      slotInfo.status !== 'Cancelled' &&
      slotInfo.status !== 'Expired' &&
      slotInfo.status !== 'Completed';

    if (isActiveAppointment) {
      setEditingSlot(slotValue);
      setAppointmentForm({
        fullName: slotInfo.fullName || '',
        identityNumber: slotInfo.identityNumber || '',
        email: slotInfo.email || '',
        phone: slotInfo.phone || '',
        publicCode: slotInfo.publicCode || generateRandomCode(),
      });
    } else {
      setEditingSlot(slotValue);
      setAppointmentForm({
        fullName: '',
        identityNumber: '',
        email: '',
        phone: '',
        publicCode: generateRandomCode(),
      });
    }
  };

  const handleCancelEdit = () => {
    setEditingSlot(null);
    setAppointmentForm({
      fullName: '',
      identityNumber: '',
      email: '',
      phone: '',
      publicCode: '',
    });
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;

    let sanitizedValue = value;

    // Input tipine göre sınırlandırma
    switch (name) {
      case 'fullName':
        // Sadece harf, boşluk ve Türkçe karakterler
        sanitizedValue = value.replace(/[^a-zA-ZçğıöşüÇĞIİÖŞÜ\s]/g, '');
        break;
      case 'identityNumber':
        // Sadece rakam, maksimum 11 karakter
        sanitizedValue = value.replace(/\D/g, '').slice(0, 11);
        break;
      case 'email':
        // Email için özel karakterler ve @ işareti izin ver
        sanitizedValue = value;
        break;
      case 'phone':
        // Telefon: Sadece rakam, maksimum 11 karakter (Türkiye telefon standardı)
        // Format: 0XXXXXXXXXX (11 haneli) veya 5XXXXXXXXX (10 haneli)
        sanitizedValue = value.replace(/\D/g, '').slice(0, 11);
        break;
      case 'publicCode':
        // PublicCode readonly olduğu için değiştirilemez
        sanitizedValue = value;
        break;
      default:
        sanitizedValue = value;
    }

    setAppointmentForm((prev) => ({ ...prev, [name]: sanitizedValue }));
  };

  const handleStaffConfirmAppointment = (appointment, closeSeatModal = false) => {
    if (!canManageSeatAppointments || !appointment?.guid || appointment.status !== 'Confirmed') {
      return;
    }

    AntdModal.confirm({
      title: 'Randevu Onayı',
      content: `${appointment.fullName || 'Seçili kullanıcı'} için randevuyu personel onaylı olarak işaretlemek istediğinize emin misiniz?`,
      okText: 'Onayla',
      cancelText: 'Vazgeç',
      onOk: async () => {
        setConfirmingAppointmentId(appointment.guid);
        setSeatError('');

        try {
          const response = await staffConfirmLibraryAppointment({ appointmentId: appointment.guid });

          if (!response?.isSuccess) {
            const errorMessage = response?.resultMessage || 'Randevu onaylanamadı.';
            setSeatError(errorMessage);
            message.error(errorMessage);
            return;
          }

          message.success(response.resultMessage || 'Randevu personel tarafından onaylandı.');

          if (closeSeatModal) {
            setSeatModalOpen(false);
            setSelectedSeat(null);
          }

          await Promise.all([
            loadSeatAppointments(seatDate, seatDisplayValues),
            loadAppointments(),
          ]);
        } catch (err) {
          const errorMessage =
            err?.response?.data?.resultMessage ||
            err?.response?.data?.message ||
            'Randevu onaylanırken bir hata oluştu.';
          setSeatError(errorMessage);
          message.error(errorMessage);
        } finally {
          setConfirmingAppointmentId(null);
        }
      },
    });
  };

  const handleCompleteAppointment = async (slotValue) => {
    if (!canManageSeatAppointments) {
      setSeatError('Bu islem icin yetkiniz yok.');
      return;
    }

    // Throttle: Prevent rapid consecutive requests (minimum 500ms between requests)
    const now = Date.now();
    const timeSinceLastRequest = now - lastProcessingTimeRef.current;
    if (isProcessing || timeSinceLastRequest < 500) {
      setSeatError('Lütfen bir önceki işlemin tamamlanmasını bekleyin.');
      return;
    }

    if (!selectedSeat || !seatDate || !slotValue) return;

    const slotInfo = selectedSeat.slots?.[slotValue];
    if (!slotInfo?.guid) {
      setSeatError('Randevu bulunamadı.');
      return;
    }

    // Sadece StaffConfirmed durumundaki randevular bitirilebilir
    if (slotInfo.status !== 'StaffConfirmed') {
      setSeatError('Sadece personel onaylı randevular bitirilebilir.');
      return;
    }

    AntdModal.confirm({
      title: 'Randevu Bitirme',
      content: 'Randevuyu bitirmek istediğinize emin misiniz? Masa boşalacak ve çıkış saati kaydedilecektir.',
      okText: 'Evet',
      cancelText: 'Hayır',
      onOk: async () => {
        setFormLoading(true);
        setIsProcessing(true);
        lastProcessingTimeRef.current = Date.now();
        setSeatError('');

        try {
          const payload = {
            status: 'Completed',
          };

          const response = await updateLibraryAppointment(slotInfo.guid, payload);

          if (response?.isSuccess) {
            message.success('Randevu başarıyla tamamlandı.');
            setSeatError('');
            // Modal'ı kapat
            setSeatModalOpen(false);
            setSelectedSeat(null);
            // Randevuları yenile
            await loadSeatAppointments(seatDate, seatDisplayValues);
            loadAppointments();
          } else {
            setSeatError(response?.resultMessage || 'Randevu tamamlanamadı.');
            message.error(response?.resultMessage || 'Randevu tamamlanamadı.');
          }
        } catch (err) {
          setSeatError('Randevu tamamlanırken bir hata oluştu.');
          message.error('Randevu tamamlanırken bir hata oluştu.');
        } finally {
          setFormLoading(false);
          setIsProcessing(false);
        }
      }
    });
  };

  const buildExtensionPayload = (appointmentId = null) => ({
    libraryId: filters.libraryId,
    appointmentDate: seatDate,
    currentSlotStartTime: selectedSlotValue,
    appointmentId,
  });

  const buildBulkCompletePayload = () => ({
    libraryId: filters.libraryId,
    appointmentDate: seatDate,
    slotStartTime: selectedSlotValue,
  });

  const renderExtensionPreview = (preview) => {
    const candidates = preview?.candidates || preview?.Candidates || [];
    if (!candidates.length) {
      return 'Seçili saat aralığında uzatılacak personel onaylı randevu bulunamadı.';
    }

    const lines = candidates.slice(0, 12).map((item) => {
      const fullName = item.fullName || item.FullName || '-';
      const currentSeat = item.currentSeatNumber || item.CurrentSeatNumber || '-';
      const targetSeat = item.targetSeatNumber || item.TargetSeatNumber;
      const canExtend = item.canExtend ?? item.CanExtend;
      const reason = item.reason || item.Reason;
      return `${fullName} | Masa ${currentSeat} -> ${canExtend ? `Masa ${targetSeat}` : reason}`;
    });

    const extraCount = Math.max(candidates.length - lines.length, 0);
    return `${lines.join('\n')}${extraCount ? `\n... ${extraCount} kişi daha` : ''}`;
  };

  const renderBulkCompletePreview = (preview) => {
    const candidates = preview?.candidates || preview?.Candidates || [];
    if (!candidates.length) {
      return 'Seçili saat aralığında bitirilecek personel onaylı randevu bulunamadı.';
    }

    const lines = candidates.slice(0, 12).map((item) => {
      const fullName = item.fullName || item.FullName || '-';
      const seatNumber = item.seatNumber || item.SeatNumber || '-';
      const publicCode = item.publicCode || item.PublicCode || '-';
      return `${fullName} | Masa ${seatNumber} | Kod ${publicCode}`;
    });

    const extraCount = Math.max(candidates.length - lines.length, 0);
    return `${lines.join('\n')}${extraCount ? `\n... ${extraCount} kişi daha` : ''}`;
  };

  const handlePreviewAndBulkCompleteAppointments = async () => {
    if (!canManageSeatAppointments) {
      setSeatError('Bu islem icin yetkiniz yok.');
      return;
    }

    if (!filters.libraryId || !seatDate || !selectedSlotValue) {
      setSeatError('Kütüphane, tarih ve saat seçimi zorunludur.');
      return;
    }

    setFormLoading(true);
    setSeatError('');

    try {
      const payload = buildBulkCompletePayload();
      const previewResponse = await previewBulkCompleteLibraryAppointments(payload);
      if (!previewResponse?.isSuccess) {
        message.error(previewResponse?.resultMessage || 'Bitirme bilgileri alınamadı.');
        return;
      }

      const preview = previewResponse.data || previewResponse.Data;
      const candidateCount = preview?.candidateCount ?? preview?.CandidateCount ?? 0;
      const slotStart = preview?.slotStartTime || preview?.SlotStartTime;
      const slotEnd = preview?.slotEndTime || preview?.SlotEndTime;

      AntdModal.confirm({
        title: 'Seçili Saat Aralığını Bitir',
        content: (
          <div className="extension-preview">
            <p>
              <strong>{slotStart} - {slotEnd}</strong> aralığındaki personel onaylı randevular tamamlandı olarak işaretlenecek.
            </p>
            <p>
              Bitirilecek randevu: <strong>{candidateCount}</strong>
            </p>
            <pre>{renderBulkCompletePreview(preview)}</pre>
          </div>
        ),
        okText: 'Onayla ve Bitir',
        cancelText: 'Vazgeç',
        okButtonProps: { disabled: candidateCount === 0 },
        onOk: async () => {
          const completeResponse = await bulkCompleteLibraryAppointments(payload);
          if (completeResponse?.isSuccess) {
            message.success(completeResponse.resultMessage || 'Seçili saat aralığındaki randevular bitirildi.');
            await loadSeatAppointments(seatDate, seatDisplayValues);
            loadAppointments();
          } else {
            message.error(completeResponse?.resultMessage || 'Randevular bitirilemedi.');
          }
        },
      });
    } catch (err) {
      message.error('Toplu bitirme işlemi sırasında bir hata oluştu.');
    } finally {
      setFormLoading(false);
    }
  };

  const handlePreviewAndExtendAppointments = async (appointmentId = null) => {
    if (!canManageSeatAppointments) {
      setSeatError('Bu islem icin yetkiniz yok.');
      return;
    }

    if (!filters.libraryId || !seatDate || !selectedSlotValue) {
      setSeatError('Kütüphane, tarih ve saat seçimi zorunludur.');
      return;
    }

    if (!nextSlotOption) {
      message.warning('Seçili saat aralığından sonra başka randevu aralığı yok.');
      return;
    }

    setFormLoading(true);
    setSeatError('');

    try {
      const payload = buildExtensionPayload(appointmentId);
      const previewResponse = await previewLibraryAppointmentExtension(payload);
      if (!previewResponse?.isSuccess) {
        message.error(previewResponse?.resultMessage || 'Uzatma bilgileri alınamadı.');
        return;
      }

      const preview = previewResponse.data || previewResponse.Data;
      const canExtendAll = preview?.canExtendAll ?? preview?.CanExtendAll;
      const candidateCount = preview?.candidateCount ?? preview?.CandidateCount ?? 0;
      const extendableCount = preview?.extendableCount ?? preview?.ExtendableCount ?? 0;
      const availableSeatCount = preview?.availableSeatCount ?? preview?.AvailableSeatCount ?? 0;
      const currentStart = preview?.currentSlotStartTime || preview?.CurrentSlotStartTime;
      const currentEnd = preview?.currentSlotEndTime || preview?.CurrentSlotEndTime;
      const nextStart = preview?.nextSlotStartTime || preview?.NextSlotStartTime;
      const nextEnd = preview?.nextSlotEndTime || preview?.NextSlotEndTime;

      AntdModal.confirm({
        title: appointmentId ? 'Randevuyu Sonraki Saate Uzat' : 'Seçili Saati Sonraki Aralığa Uzat',
        content: (
          <div className="extension-preview">
            <p>
              <strong>{currentStart} - {currentEnd}</strong> aralığındaki randevular
              <strong> {nextStart} - {nextEnd}</strong> aralığına taşınacak.
            </p>
            <p>
              Uygun masa: <strong>{availableSeatCount}</strong> | Uzatılacak: <strong>{extendableCount}/{candidateCount}</strong>
            </p>
            <pre>{renderExtensionPreview(preview)}</pre>
            {!canExtendAll && (
              <p className="extension-warning">Tüm randevular için yeterli uygun masa bulunmadığı için işlem yapılamaz.</p>
            )}
          </div>
        ),
        okText: 'Onayla ve Uzat',
        cancelText: 'Vazgeç',
        okButtonProps: { disabled: !canExtendAll || candidateCount === 0 },
        onOk: async () => {
          const extendResponse = await extendLibraryAppointments(payload);
          if (extendResponse?.isSuccess) {
            message.success(extendResponse.resultMessage || 'Randevu sonraki saat aralığına uzatıldı.');
            setSeatModalOpen(false);
            setSelectedSeat(null);
            await loadSeatAppointments(seatDate, seatDisplayValues);
            loadAppointments();
          } else {
            message.error(extendResponse?.resultMessage || 'Randevu uzatılamadı.');
          }
        },
      });
    } catch (err) {
      message.error('Uzatma işlemi sırasında bir hata oluştu.');
    } finally {
      setFormLoading(false);
    }
  };

  const handleSaveAppointment = async (slotValue) => {
    if (!canManageSeatAppointments) {
      setSeatError('Bu islem icin yetkiniz yok.');
      return;
    }

    if (!selectedSeat || !seatDate || !slotValue) return;

    // Validasyon kontrolleri - Modal içinde gösterilecek
    setSeatError('');

    if (!appointmentForm.fullName || !appointmentForm.identityNumber) {
      setSeatError('Ad Soyad ve T.C. Kimlik alanları zorunludur.');
      return;
    }

    // Ad Soyad validasyonu
    if (appointmentForm.fullName.trim().length < 2) {
      setSeatError('Ad Soyad en az 2 karakter olmalıdır.');
      return;
    }

    // T.C. Kimlik validasyonu
    if (appointmentForm.identityNumber.length !== 11) {
      setSeatError('T.C. Kimlik numarası 11 haneli olmalıdır.');
      return;
    }

    // T.C. Kimlik ilk hanesi 0 olamaz
    if (appointmentForm.identityNumber[0] === '0') {
      setSeatError('T.C. Kimlik numarası 0 ile başlayamaz.');
      return;
    }

    // E-Posta veya Telefon alanlarından en az biri doldurulmalı
    const hasEmail = appointmentForm.email && appointmentForm.email.trim();
    const hasPhone = appointmentForm.phone && appointmentForm.phone.trim();

    if (!hasEmail && !hasPhone) {
      setSeatError('E-Posta veya Telefon alanlarından en az biri doldurulmalıdır.');
      return;
    }

    // E-Posta validasyonu (doldurulmuşsa geçerli olmalı)
    if (hasEmail) {
      const emailRegex = /^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}$/;
      if (!emailRegex.test(appointmentForm.email.trim())) {
        setSeatError('Geçerli bir e-posta adresi giriniz.');
        return;
      }
    }

    // Telefon validasyonu (doldurulmuşsa geçerli olmalı)
    if (hasPhone) {
      const phoneDigits = appointmentForm.phone.replace(/\D/g, '');
      // Türkiye telefon standardı: 10 veya 11 haneli
      if (phoneDigits.length !== 10 && phoneDigits.length !== 11) {
        setSeatError('Telefon numarası 10 veya 11 haneli olmalıdır.');
        return;
      }
      // İlk hane kontrolü (Türkiye telefon standardı)
      if (phoneDigits.length === 11 && phoneDigits[0] !== '0') {
        setSeatError('11 haneli telefon numarası 0 ile başlamalıdır (örn: 05321234567).');
        return;
      }
      if (phoneDigits.length === 10 && phoneDigits[0] !== '5') {
        setSeatError('10 haneli telefon numarası 5 ile başlamalıdır (örn: 5321234567).');
        return;
      }
    }

    // Aynı TC, Email veya Telefon ile gün içinde randevu kontrolü
    const existingAppointment = selectedSeat.slots?.[slotValue];
    if (!existingAppointment?.guid) {
      try {
        const checkResponse = await fetchLibraryAppointments({
          ...defaultFilters,
          startDate: seatDate,
          endDate: seatDate,
          libraryId: filters.libraryId,
          page: 1,
          pageSize: 1000,
        });

        if (checkResponse?.isSuccess) {
          const data = checkResponse.data || checkResponse.Data;
          const items = data?.items || data?.Items || [];
          const hasDuplicate = items.some((item) => {
            const sameIdentity = item.identityNumber === appointmentForm.identityNumber;
            const sameEmail = hasEmail && item.email && item.email.trim().toLowerCase() === appointmentForm.email.trim().toLowerCase();
            const samePhone = hasPhone && item.phone && item.phone.replace(/\D/g, '') === appointmentForm.phone.replace(/\D/g, '');
            // İptal edilmemiş ve süresi dolmamış randevuları kontrol et
            const isActive = item.status &&
              item.status !== 'Cancelled' &&
              item.status !== 'Expired';
            return isActive && (sameIdentity || sameEmail || samePhone);
          });

          if (hasDuplicate) {
            setSeatError('Bu TC Kimlik, E-Posta veya Telefon numarası ile bugün için zaten aktif bir randevu bulunmaktadır.');
            return;
          }
        }
      } catch (err) {
        // Kontrol sırasında hata oluşursa devam et
        console.warn('Duplicate check failed:', err);
      }
    }

    setFormLoading(true);
    setSeatError('');

    try {
      const slotOption = slotOptions.find((opt) => opt.value === slotValue);
      if (!slotOption) {
        setSeatError('Geçersiz randevu saati.');
        setFormLoading(false);
        return;
      }

      const startMinutes = parseTimeToMinutes(slotOption.value);
      const duration = Number(settings?.slotDurationMinutes) || 240;
      const dayEndMinutes = parseTimeToMinutes(settings?.workingDayEndTime || '18:00');
      // Fix for 24:00/23:59 logic in end time
      const effectiveDayEnd = dayEndMinutes === 0 ? 1440 : (dayEndMinutes === 1439 ? 1440 : dayEndMinutes);

      let nextLimit = startMinutes + duration;
      let endMinutes;

      // Same logic as generation: if remainder is small, take it all
      if (effectiveDayEnd - nextLimit < duration) {
        endMinutes = effectiveDayEnd;
      } else {
        endMinutes = nextLimit > effectiveDayEnd ? effectiveDayEnd : nextLimit;
      }

      // Eğer end time 1440 dakika (24:00) veya üzeriyse, 23:59:59 olarak gönder
      // Çünkü SQL'de mesai bitişi 23:59:59 olarak saklanıyor ve 24:00 geçerli değil
      let slotEndTime;
      if (endMinutes >= 1440) {
        slotEndTime = '23:59:59'; // SQL formatına uygun
      } else {
        slotEndTime = formatMinutesToTime(endMinutes);
      }

      const payload = {
        appointmentDate: seatDate,
        seatNumber: selectedSeat.seatNumber,
        slotStartTime: slotOption.value,
        slotEndTime,
        fullName: appointmentForm.fullName,
        identityNumber: appointmentForm.identityNumber,
        email: appointmentForm.email || null,
        phone: appointmentForm.phone || null,
        publicCode: appointmentForm.publicCode || generateRandomCode(),
        status: 'StaffConfirmed',
        libraryId: filters.libraryId
      };

      let response;
      if (existingAppointment?.guid) {
        response = await updateLibraryAppointment(existingAppointment.guid, payload);
      } else {
        response = await createLibraryAppointment(payload);
      }

      if (response?.isSuccess) {
        message.success(response.resultMessage || 'Randevu kaydedildi.');
        setSeatError('');
        handleCancelEdit();
        // Modal'ı kapat
        setSeatModalOpen(false);
        setSelectedSeat(null);
        // Randevuları yenile
        await loadSeatAppointments(seatDate, seatDisplayValues);
        loadAppointments();
      } else {
        setSeatError(response?.resultMessage || 'Randevu kaydedilemedi.');
        message.error(response?.resultMessage || 'Randevu kaydedilemedi.');
      }
    } catch (err) {
      setSeatError('Randevu kaydedilirken bir hata oluştu.');
    } finally {
      setFormLoading(false);
    }
  };

  const seatModal =
    modalContainer && isSeatModalOpen && selectedSeat
      ? createPortal(
        <div className="seat-modal-overlay" onClick={handleCloseSeatModal}>
          <div
            className="seat-modal"
            role="dialog"
            aria-modal="true"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="seat-modal-header">
              <div>
                <p className="seat-modal-label">Seçilen Masa</p>
                <h4>{selectedSeat.seatNumber}. Masa</h4>
                <span>{seatDate ? new Date(seatDate).toLocaleDateString('tr-TR') : ''}</span>
              </div>
              <button type="button" className="seat-modal-close" aria-label="Kapat" onClick={handleCloseSeatModal}>
                &times;
              </button>
            </div>
            {seatError && (
              <div className="seat-modal-error">
                {seatError}
              </div>
            )}
            <div className="seat-modal-grid">
              {selectedSlotValue && (() => {
                const slot = seatDisplaySlots.find(s => s.value === selectedSlotValue);
                if (!slot) return null;

                const slotInfo = selectedSeat.slots?.[selectedSlotValue];
                // İptal edilen, süresi dolmuş veya tamamlanmış randevuları boş olarak göster
                const isActiveAppointment = slotInfo &&
                  slotInfo.status &&
                  slotInfo.status !== 'Cancelled' &&
                  slotInfo.status !== 'Expired' &&
                  slotInfo.status !== 'Completed';
                const displayStatus = isActiveAppointment ? slotInfo.status : null;
                const statusText = getStatusText(displayStatus);
                const statusClass = displayStatus ? displayStatus.toLowerCase() : 'available';
                const index = seatDisplaySlots.findIndex(s => s.value === selectedSlotValue);

                return (
                  <div key={slot.value} className="seat-slot-card">
                    <div className="seat-slot-card-header">
                      <div>
                        <span className="seat-slot-card-position">{`${index + 1}. Randevu`}</span>
                        <strong>{slot.label}</strong>
                      </div>
                      <span className={`status status-${statusClass}`}>
                        {statusText}
                      </span>
                    </div>
                    {editingSlot === selectedSlotValue ? (
                      <div className="seat-slot-card-form">
                        <div className="form-field">
                          <label>Ad Soyad *</label>
                          <input
                            type="text"
                            name="fullName"
                            value={appointmentForm.fullName}
                            onChange={handleFormChange}
                            pattern="[a-zA-ZçğıöşüÇĞIİÖŞÜ\s]+"
                            title="Sadece harf karakterleri kullanılabilir"
                            maxLength={100}
                            required
                          />
                        </div>
                        <div className="form-field">
                          <label>T.C. Kimlik *</label>
                          <input
                            type="text"
                            name="identityNumber"
                            value={appointmentForm.identityNumber}
                            onChange={handleFormChange}
                            pattern="[0-9]{11}"
                            title="11 haneli T.C. Kimlik numarası giriniz"
                            maxLength={11}
                            inputMode="numeric"
                            required
                          />
                        </div>
                        <div className="form-field">
                          <label>
                            E-Posta
                            {!appointmentForm.phone?.trim() && <span className="text-danger"> *</span>}
                          </label>
                          <input
                            type="email"
                            name="email"
                            value={appointmentForm.email}
                            onChange={handleFormChange}
                            pattern="[a-z0-9._%+\-]+@[a-z0-9.\-]+\.[a-z]{2,}$"
                            title="Geçerli bir e-posta adresi giriniz"
                            maxLength={150}
                          />
                        </div>
                        <div className="form-field">
                          <label>
                            Telefon
                            {!appointmentForm.email?.trim() && <span className="text-danger"> *</span>}
                          </label>
                          <input
                            type="tel"
                            name="phone"
                            value={appointmentForm.phone}
                            onChange={handleFormChange}
                            pattern="[0-9]{10,11}"
                            title="10 veya 11 haneli telefon numarası giriniz (örn: 05321234567)"
                            maxLength={11}
                            inputMode="numeric"
                            placeholder="05321234567"
                          />
                        </div>
                        <div className="form-field">
                          <label>Randevu Kodu</label>
                          <input
                            type="text"
                            name="publicCode"
                            value={appointmentForm.publicCode}
                            onChange={handleFormChange}
                            readOnly
                          />
                        </div>
                        <div className="form-actions">
                          <button
                            type="button"
                            className="btn btn-light btn-compact"
                            onClick={handleCancelEdit}
                            disabled={formLoading}
                          >
                            İptal
                          </button>
                          <button
                            type="button"
                            className="btn btn-primary btn-compact"
                            onClick={() => handleSaveAppointment(selectedSlotValue)}
                            disabled={formLoading}
                          >
                            {formLoading ? 'Kaydediliyor...' : 'Kaydet'}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        {slotInfo && slotInfo.status &&
                          slotInfo.status !== 'Cancelled' &&
                          slotInfo.status !== 'Expired' &&
                          slotInfo.status !== 'Completed' ? (
                          <div className="seat-slot-card-body">
                            <p>
                              <span>Ad Soyad</span>
                              <strong>{slotInfo.fullName}</strong>
                            </p>
                            <p>
                              <span>T.C. Kimlik</span>
                              <strong>{slotInfo.identityNumber}</strong>
                            </p>
                            <p>
                              <span>E-Posta</span>
                              <strong>{slotInfo.email || '-'}</strong>
                            </p>
                            <p>
                              <span>Telefon</span>
                              <strong>{slotInfo.phone || '-'}</strong>
                            </p>
                            {settings?.qrModeEnabled !== false && (
                              <p>
                                <span>Randevu Kodu</span>
                                <strong>{slotInfo.publicCode || '-'}</strong>
                              </p>
                            )}
                            {slotInfo.confirmedAt && (
                              <p>
                                <span>Mail Onay Saati</span>
                                <strong>
                                  {formatDateTime(slotInfo.confirmedAt, {
                                    day: '2-digit',
                                    month: '2-digit',
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                  })}
                                </strong>
                              </p>
                            )}
                            {slotInfo.staffConfirmedAt && (
                              <p>
                                <span>Kütüphaneye Giriş Saati</span>
                                <strong>
                                  {formatDateTime(slotInfo.staffConfirmedAt, {
                                    day: '2-digit',
                                    month: '2-digit',
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                  })}
                                </strong>
                              </p>
                            )}
                            {slotInfo.completedAt && (
                              <p>
                                <span>Çıkış Saati</span>
                                <strong>
                                  {formatDateTime(slotInfo.completedAt, {
                                    day: '2-digit',
                                    month: '2-digit',
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                  })}
                                </strong>
                              </p>
                            )}
                          </div>
                        ) : (
                          <p className="seat-slot-card-empty">Bu saat için rezervasyon bulunmuyor.</p>
                        )}
                        {canManageSeatAppointments && (
                          <div className="seat-slot-card-actions">
                            {slotInfo && slotInfo.status === 'Confirmed' && (
                              <button
                                type="button"
                                className="btn btn-success btn-compact"
                                onClick={() => handleStaffConfirmAppointment(slotInfo, true)}
                                disabled={confirmingAppointmentId === slotInfo.guid}
                              >
                                {confirmingAppointmentId === slotInfo.guid ? 'Onaylanıyor...' : 'Randevuyu Onayla'}
                              </button>
                            )}
                            {slotInfo && slotInfo.status === 'StaffConfirmed' && (
                              <>
                                <button
                                  type="button"
                                  className="btn btn-success btn-compact"
                                  onClick={() => handleCompleteAppointment(selectedSlotValue)}
                                  disabled={formLoading}
                                >
                                  {formLoading ? 'İşleniyor...' : 'Randevuyu Bitir'}
                                </button>
                                {nextSlotOption && (
                                  <button
                                    type="button"
                                    className="btn btn-light btn-compact"
                                    onClick={() => handlePreviewAndExtendAppointments(slotInfo.guid)}
                                    disabled={formLoading}
                                  >
                                    Sonraki saate uzat
                                  </button>
                                )}
                              </>
                            )}
                            <button
                              type="button"
                              className="btn btn-primary btn-compact"
                              onClick={() => handleEditSlot(selectedSlotValue, slotInfo)}
                              disabled={formLoading}
                            >
                              {slotInfo && slotInfo.status &&
                                slotInfo.status !== 'Cancelled' &&
                                slotInfo.status !== 'Expired' &&
                                slotInfo.status !== 'Completed'
                                ? 'Düzenle' : 'Randevu Ekle'}
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })()}
              {!selectedSlotValue && (
                <div className="seat-slot-card seat-slot-card-empty">Randevu saati seçilmedi.</div>
              )}
            </div>
          </div>
        </div>,
        modalContainer
      )
      : null;

  const loadSeatAppointments = useCallback(
    async (dateValue, slotValues) => {
      const targetDate = dateValue || seatDate;
      const activeSlots = (slotValues && slotValues.length ? slotValues : seatDisplayValues).filter(Boolean);

      if (!activeSlots.length) {
        setSeatAppointments({});
        return;
      }

      setSeatLoading(true);
      setSeatError('');

      try {
        const responses = await Promise.all(
          activeSlots.map((slot) =>
            fetchLibraryAppointments({
              ...defaultFilters,
              startDate: targetDate,
              endDate: targetDate,
              slotStartTime: slot,
              libraryId: filters.libraryId,
              page: 1,
              pageSize: DEFAULT_SEAT_PAGE_SIZE,
            })
          )
        );

        const grouped = {};
        responses.forEach((response, index) => {
          const slot = activeSlots[index];
          if (response?.isSuccess) {
            const data = response.data || response.Data;
            const items = data?.items || data?.Items || [];
            items.forEach((item) => {
              // İptal edilen, süresi dolmuş ve tamamlanmış randevuları masa planında gösterme
              const status = item?.status;
              if (status === 'Cancelled' || status === 'Expired' || status === 'Completed') {
                return;
              }

              const seatNumber = Number(item?.seatNumber);
              if (!Number.isFinite(seatNumber)) return;
              if (!grouped[seatNumber]) grouped[seatNumber] = {};
              grouped[seatNumber][slot] = item;
            });
          } else if (response) {
            setSeatError(response?.resultMessage || 'Masa planı yüklenemedi.');
          }
        });

        setSeatAppointments(grouped);
      } catch (err) {
        setSeatError('Masa planı yüklenirken bir hata oluştu.');
        setSeatAppointments({});
      } finally {
        setSeatLoading(false);
      }
    },
    [seatDate, seatDisplayValues]
  );

  const loadAppointments = async (override = {}) => {
    setLoading(true);

    try {
      // İstek yapılırken hangi sayfayı istediğimizi sakla
      const requestedPage = override.page !== undefined ? override.page : filters.page;
      const finalFilters = { ...filters, ...override };
      if (!finalFilters.libraryId || finalFilters.libraryId === 1) {
        setLoading(false);
        return;
      }
      const response = await fetchLibraryAppointments(finalFilters);

      if (response?.isSuccess) {
        const data = response.data || response.Data;
        setAppointments(data?.items || data?.Items || []);

        // Backend'den gelen veya istediğimiz sayfa numarasını kullan
        const pageNumber = data?.pageNumber || data?.PageNumber || requestedPage;
        const pageSize = data?.pageSize || data?.PageSize || filters.pageSize;

        setPagination({
          totalCount: data?.totalCount || data?.TotalCount || 0,
          pageNumber: pageNumber,
          pageSize: pageSize,
        });

        setFilters((prev) => ({
          ...prev,
          ...override,
          page: pageNumber,
          pageSize: pageSize,
        }));
      } else {
        message.error(response?.resultMessage || 'Randevular yüklenemedi.');
      }
    } catch (err) {
      message.error('Randevular yüklenirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const loadSettings = async (libraryId) => {
    if (!libraryId || libraryId === 1) return;
    setSettingsLoading(true);
    try {
      const response = await getLibraryAppointmentSettings(libraryId || filters.libraryId);
      if (response?.isSuccess) {
        const normalized = normalizeSettings(response.data || response.Data || {});
        setSettings(normalized);
        // QR Mode ayarını localStorage'a kaydet
        if (normalized?.qrModeEnabled !== undefined) {
          localStorage.setItem('libraryQrModeEnabled', String(normalized.qrModeEnabled));
          // Custom event dispatch for same-tab updates
          window.dispatchEvent(new Event('libraryQrModeChanged'));
        }
      }
    } catch (err) {
      message.error('Randevu ayarları yüklenemedi.');
    } finally {
      setSettingsLoading(false);
    }
  };

  const loadLibraries = async () => {
    setLibrariesLoading(true);
    try {
      const response = await getLibraries();
      if (response?.isSuccess) {
        const libs = response.data || [];

        // Library-only kullanıcılar için sadece kendi kütüphanelerini göster
        const userLibraryId = getLibraryId();
        const libraryOnly = isLibraryOnlyUser();

        if (libraryOnly && userLibraryId) {
          // Sadece kendi kütüphanesini listede tutalım
          const filtered = libs.filter(l => l.id === userLibraryId);
          setLibraries(filtered.length > 0 ? filtered : libs);
          setFilters(prev => ({ ...prev, libraryId: userLibraryId }));
        } else {
          setLibraries(libs);
          // Auto-select the first library for admin users
          if (libs.length > 0 && (!filters.libraryId || filters.libraryId === 1)) {
            setFilters(prev => ({ ...prev, libraryId: libs[0].id }));
          }
        }
      }
    } catch (err) {
      console.error('Kütüphaneler yüklenemedi:', err);
    } finally {
      setLibrariesLoading(false);
    }
  };

  useEffect(() => {
    loadLibraries();
  }, [isAdmin]); // initial load only

  useEffect(() => {
    if (filters.libraryId && filters.libraryId !== 1) {
      loadSettings(filters.libraryId);
      loadAppointments({ page: 1 });
    }
  }, [filters.libraryId]);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!seatDisplayValues.length) {
      setSeatAppointments({});
      return;
    }

    // Modal açıkken refresh yapma
    if (!isSeatModalOpen) {
      loadSeatAppointments(seatDate, seatDisplayValues);
    }
  }, [seatDate, seatDisplayValues, loadSeatAppointments, isSeatModalOpen]);

  // Tarih değiştiğinde modal'ı kapat (sadece modal açıksa)
  useEffect(() => {
    if (isSeatModalOpen) {
      setSelectedSeat(null);
      setSeatModalOpen(false);
    }
  }, [seatDate]);

  useEffect(() => {
    if (!modalContainer || !isSeatModalOpen) {
      return undefined;
    }
    const previousOverflow = modalContainer.style.overflow;
    modalContainer.style.overflow = 'hidden';
    return () => {
      modalContainer.style.overflow = previousOverflow;
    };
  }, [isSeatModalOpen, modalContainer]);

  useEffect(() => {
    loadAppointmentsRef.current = loadAppointments;
  });

  useEffect(() => {
    const interval = setInterval(() => {
      if (typeof loadAppointmentsRef.current === 'function') {
        loadAppointmentsRef.current();
      }
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!searchInitializedRef.current) {
      searchInitializedRef.current = true;
      return undefined;
    }

    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    searchDebounceRef.current = setTimeout(() => {
      if (typeof loadAppointmentsRef.current === 'function') {
        loadAppointmentsRef.current({ page: 1, search: filters.search });
      }
    }, 500);

    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
        searchDebounceRef.current = null;
      }
    };
  }, [filters.search]);

  const seatOccupancy = useMemo(() => {
    const totalSeats = settings?.maxSeatsPerDay || 95;
    const totalSlots = seatDisplaySlots.length * totalSeats;
    if (!totalSlots) {
      return {
        total: 0,
        occupied: 0,
        available: 0,
        percentage: 0,
      };
    }

    let occupied = 0;
    seatDisplaySlots.forEach((slot) => {
      const slotValue = slot.value;
      if (!slotValue) return;
      Object.keys(seatInfoMap).forEach((seatKey) => {
        const slotInfo = seatInfoMap[seatKey]?.[slotValue];
        if (slotInfo && slotInfo.status && !['Cancelled', 'Expired'].includes(slotInfo.status)) {
          occupied += 1;
        } else if (slotInfo && !slotInfo.status) {
          occupied += 1;
        }
      });
    });

    const available = Math.max(totalSlots - occupied, 0);
    const percentage = Math.round((occupied / totalSlots) * 100);

    return {
      total: totalSlots,
      occupied,
      available,
      percentage,
    };
  }, [seatInfoMap, seatDisplaySlots]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleDateFilterChange = (value) => {
    setFilters((prev) => {
      const next = {
        ...prev,
        startDate: value,
        endDate: value,
      };
      loadAppointments({ ...next, page: 1 });
      return next;
    });
  };

  const handleDelete = async (appointment) => {
    AntdModal.confirm({
      title: 'Randevu Silme',
      content: `${appointment.fullName} için randevuyu silmek istediğinize emin misiniz?`,
      okText: 'Evet',
      okType: 'danger',
      cancelText: 'Hayır',
      onOk: async () => {
        try {
          const response = await deleteLibraryAppointment(appointment.guid);
          if (response?.isSuccess) {
            message.success(response.resultMessage || 'Randevu silindi.');
            loadAppointments();
          } else {
            message.error(response?.resultMessage || 'Randevu silinemedi.');
          }
        } catch (err) {
          message.error('Randevu silinirken bir hata oluştu.');
        }
      }
    });
  };

  const handleSaveSettings = async (e) => {
    e.preventDefault();
    if (!settings) return;

    setSettingsLoading(true);
    try {
      const response = await updateLibraryAppointmentSettings({
        maxSeatsPerDay: Number(settings.maxSeatsPerDay),
        minDaysBetweenBookings: Number(settings.minDaysBetweenBookings),
        verificationCodeExpiryMinutes: Number(settings.verificationCodeExpiryMinutes),
        workingDayStartTime: settings.workingDayStartTime,
        workingDayEndTime: settings.workingDayEndTime,
        slotDurationMinutes: Number(settings.slotDurationMinutes),
        qrModeEnabled: settings.qrModeEnabled,
        libraryId: filters.libraryId
      });

      if (response?.isSuccess) {
        message.success(response.resultMessage || 'Ayarlar güncellendi.');
        loadSettings();
        // QR Mode ayarını localStorage'a kaydet
        if (settings?.qrModeEnabled !== undefined) {
          localStorage.setItem('libraryQrModeEnabled', String(settings.qrModeEnabled));
          // Custom event dispatch for same-tab updates
          window.dispatchEvent(new Event('libraryQrModeChanged'));
        }
      } else {
        message.error(response?.resultMessage || 'Ayarlar güncellenemedi.');
      }
    } catch (err) {
      message.error('Ayarlar güncellenirken bir hata oluştu.');
    } finally {
      setSettingsLoading(false);
    }
  };

  return (
    <div className="library-appointments">
      <div className="page-header">
        <h2>Kütüphane Randevuları</h2>
        <div className="actions">
          <div className="library-select-wrapper">
            <label>Kütüphane:</label>
            {isLibraryOnlyUser() ? (
              <span style={{
                fontWeight: 600,
                color: '#0284c7',
                fontSize: '0.95rem',
                padding: '0.35rem 0.75rem',
                background: '#f0f9ff',
                border: '1px solid #bae6fd',
                borderRadius: '6px',
              }}>
                {libraries.find(l => l.id === filters.libraryId)?.name || 'Yükleniyor...'}
              </span>
            ) : (
              <select
                name="libraryId"
                value={filters.libraryId}
                onChange={handleFilterChange}
                disabled={librariesLoading}
              >
                {libraries.map(lib => (
                  <option key={lib.id} value={lib.id}>{lib.name}</option>
                ))}
              </select>
            )}
          </div>
          <button className="btn btn-secondary" onClick={() => loadAppointments()}>
            Yenile
          </button>
        </div>
      </div>



      <section className="seat-section">
        <div className="seat-section-header">
          <div className="seat-section-info">
            <h3>Masa Planı</h3>
            <p>Sol yarı ilk randevu saatini, sağ yarı ikinci randevu saatini temsil eder. Renkler randevu durumunu gösterir.</p>
          </div>
          <div className="seat-section-stats">
            <div className="seat-summary-item seat-summary-date">
              <span className="seat-summary-label">Tarih</span>
              <strong>{seatDate ? new Date(seatDate).toLocaleDateString('tr-TR') : '-'}</strong>
              <button
                type="button"
                className="seat-date-icon-button"
                onClick={() => seatDateInputRef.current?.showPicker?.()}
                aria-label="Masa planı tarihini seç"
                title="Tarih seç"
              >
                <svg viewBox="0 0 24 24" role="img" aria-hidden="true" focusable="false">
                  <path
                    d="M7 2v3M17 2v3M4.5 7h15M5 5h14a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2z"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <rect x="7" y="11" width="3" height="3" rx="0.75" />
                  <rect x="11" y="11" width="3" height="3" rx="0.75" />
                  <rect x="15" y="11" width="3" height="3" rx="0.75" />
                </svg>
              </button>
              <input
                ref={seatDateInputRef}
                type="date"
                value={seatDate}
                max="2099-12-31"
                onChange={(e) => setSeatDate(e.target.value)}
                className="seat-summary-date-input"
                aria-label="Masa planı tarihi seç"
              />
            </div>
            <div className="seat-summary-item">
              <span className="seat-summary-label">Doluluk</span>
              <strong>{seatOccupancy.percentage}%</strong>
              <small>
                {seatOccupancy.occupied}/{seatOccupancy.total}
              </small>
            </div>
            <div className="seat-summary-item">
              <span className="seat-summary-label">Saat</span>
              <strong>
                {formatDateTime(currentTime, {
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit',
                })}
              </strong>
              <small>{formatDateTime(currentTime, { day: '2-digit', month: '2-digit', year: 'numeric' })}</small>
            </div>
          </div>
          <div className="seat-section-controls">
            <button
              className="btn btn-primary btn-compact"
              onClick={() => handlePreviewAndExtendAppointments()}
              disabled={seatLoading || formLoading || !selectedSlotValue || !nextSlotOption}
              title={nextSlotOption ? 'Seçili saat aralığındaki içeride olanları sonraki aralığa uzat' : 'Sonraki randevu aralığı yok'}
            >
              Sonraki saate uzat
            </button>
            <button
              className="btn btn-warning btn-compact"
              onClick={() => handlePreviewAndBulkCompleteAppointments()}
              disabled={seatLoading || formLoading || !selectedSlotValue}
              title="Seçili saat aralığındaki içeride olan tüm randevuları bitir"
            >
              Tüm randevuları bitir
            </button>
            <button
              className="btn btn-light btn-compact seat-refresh-button"
              onClick={() => loadSeatAppointments(seatDate, seatDisplayValues)}
              disabled={seatLoading || !seatDisplayValues.length}
              aria-label="Masa planını yenile"
              title="Masa planını yenile"
            >
              {seatLoading ? (
                <span className="seat-refresh-spinner" />
              ) : (
                <svg
                  className="seat-refresh-icon"
                  viewBox="0 0 24 24"
                  role="img"
                  aria-hidden="true"
                  focusable="false"
                >
                  <path
                    d="M5.64 5.64a9 9 0 1 1-.01 12.72"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <polyline
                    points="5.64 2 5.64 8 11.64 8"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </button>
          </div>
        </div>

        {seatError && <div className="seat-alert">{seatError}</div>}

        {settings?.qrModeEnabled !== false && (
          <div className="seat-legend">
            <div className="seat-legend-item">
              <span className="seat-legend-dot staff" />
              <span>Personel Onaylı</span>
            </div>
            <div className="seat-legend-item">
              <span className="seat-legend-dot user" />
              <span>Kullanıcı Onaylı</span>
            </div>
            <div className="seat-legend-item">
              <span className="seat-legend-dot pending" />
              <span>Beklemede</span>
            </div>
            <div className="seat-legend-item">
              <span className="seat-legend-dot inactive" />
              <span>İptal / Süresi Doldu</span>
            </div>
            <div className="seat-legend-item">
              <span className="seat-legend-dot available" />
              <span>Müsait</span>
            </div>
          </div>
        )}

        {seatDisplaySlots.length > 0 ? (
          <>
            <div className="seat-slot-legend">
              {seatDisplaySlots.map((slot, index) => (
                <button
                  key={slot.value}
                  type="button"
                  className={`seat-slot-legend-item ${selectedSlotValue === slot.value ? 'active' : ''}`}
                  onClick={() => setSelectedSlotValue(slot.value)}
                >
                  <span className="seat-slot-legend-position">{`${index + 1}. Randevu`}</span>
                  <strong>{slot.label}</strong>
                </button>
              ))}
            </div>

            <div className="seat-table-wrapper" ref={seatTableWrapperRef}>
              {seatLoading && <div className="seat-loading">Masa planı yükleniyor...</div>}
              <table className="seat-table">
                <tbody>
                  {seatRows.map((row, rowIndex) => (
                    <tr key={`seat-row-${rowIndex}`}>
                      {row.map((seatNumber, cellIndex) =>
                        seatNumber ? (
                          <td key={seatNumber} className="seat-cell">
                            <div
                              role="button"
                              tabIndex={0}
                              onKeyDown={(event) => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                  event.preventDefault();
                                  handleSeatClick(seatNumber);
                                }
                              }}
                              onClick={() => handleSeatClick(seatNumber)}
                              className={[
                                'seat',
                                selectedSeat?.seatNumber === seatNumber ? 'seat-selected' : '',
                              ]
                                .filter(Boolean)
                                .join(' ')}
                              aria-label={`${seatNumber}. masa`}
                            >
                              <div className="chair-top-view">
                                {selectedSlotValue && (() => {
                                  const slotInfo = seatInfoMap[seatNumber]?.[selectedSlotValue];
                                  // Completed statüsündeki randevular boş masa olarak gösterilir
                                  const isCompleted = slotInfo?.status === 'Completed';
                                  const modifier = isCompleted ? 'status-available' : getStatusModifier(slotInfo?.status);
                                  const statusLabel = isCompleted
                                    ? 'Müsait'
                                    : (slotInfo?.status
                                      ? STATUS_LABELS[slotInfo.status] || slotInfo.status
                                      : 'Müsait');
                                  const selectedSlot = seatDisplaySlots.find(s => s.value === selectedSlotValue);
                                  const title = selectedSlot ? `${selectedSlot.label} - ${statusLabel}` : statusLabel;

                                  return (
                                    <div className={`chair-seat-top ${modifier}`} title={title}>
                                      <span className="seat-number">{seatNumber}</span>
                                    </div>
                                  );
                                })()}
                                {!selectedSlotValue && (
                                  <div className="chair-seat-top">
                                    <span className="seat-number">{seatNumber}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                        ) : (
                          <td key={`empty-${rowIndex}-${cellIndex}`} className="seat-cell seat-cell-empty" />
                        )
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <p className="seat-details-empty">Mesai saatleri tanımlanmadı.</p>
        )}
      </section>

      <section className="filters">
        <div className="field">
          <label>Tarih</label>
          <input
            type="date"
            name="startDate"
            value={filters.startDate}
            onChange={(e) => handleDateFilterChange(e.target.value)}
          />
        </div>
        <div className="field">
          <label>Arama</label>
          <input
            type="text"
            placeholder="İsim, TC, e-posta, telefon"
            name="search"
            value={filters.search}
            onChange={handleFilterChange}
          />
        </div>
        <div className="field">
          <label>Durum</label>
          <select name="status" value={filters.status} onChange={handleFilterChange}>
            {statusOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>Randevu Saati</label>
          <select
            name="slotStartTime"
            value={filters.slotStartTime}
            onChange={handleFilterChange}
            disabled={!slotOptions.length}
          >
            <option value="">Tüm Saatler</option>
            {slotOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        <div className="field field-actions single-action">
          <button
            className="btn btn-light"
            onClick={() => {
              setFilters(defaultFilters);
              loadAppointments(defaultFilters);
            }}
          >
            Sıfırla
          </button>
        </div>
      </section>

      <section className="table-wrapper">
        <div className="table-responsive">
          <table>
            <thead>
              <tr>
                <th>Ad Soyad</th>
                <th>TC</th>
                <th>Tarih</th>
                <th>Saat</th>
                <th>Masa</th>
                {settings?.qrModeEnabled !== false && <th>Kod</th>}
                <th>Durum</th>
                <th>Giriş Saati</th>
                <th>Çıkış Saati</th>
                <th>E-Posta</th>
                <th>Telefon</th>
                <th>Olusturma</th>
                <th>İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={settings?.qrModeEnabled !== false ? 13 : 12} className="text-center">
                    Yükleniyor…
                  </td>
                </tr>
              ) : appointments.length === 0 ? (
                <tr>
                  <td colSpan={settings?.qrModeEnabled !== false ? 13 : 12} className="text-center">
                    Kayıt bulunamadı.
                  </td>
                </tr>
              ) : (
                appointments.map((item) => (
                  <tr key={item.guid}>
                    <td>{item.fullName}</td>
                    <td>{item.identityNumber}</td>
                    <td>{new Date(item.appointmentDate).toLocaleDateString('tr-TR')}</td>
                    <td>
                      {item.slotStartTime && item.slotEndTime
                        ? `${item.slotStartTime} - ${item.slotEndTime}`
                        : '-'}
                    </td>
                    <td>{item.seatNumber}</td>
                    {settings?.qrModeEnabled !== false && (
                      <td className="public-code">{item.publicCode || '-'}</td>
                    )}
                    <td>
                      <span className={`status status-${item.status?.toLowerCase()}`}>
                        {STATUS_LABELS[item.status] || item.status}
                      </span>
                    </td>
                    <td>
                      {item.staffConfirmedAt
                        ? formatDateTime(item.staffConfirmedAt, {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })
                        : '-'}
                    </td>
                    <td>
                      {item.completedAt
                        ? formatDateTime(item.completedAt, {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })
                        : '-'}
                    </td>
                    <td>{item.email}</td>
                    <td>{item.phone}</td>
                    <td>
                      {item.createDate
                        ? new Date(item.createDate).toLocaleString('tr-TR')
                        : '-'}
                    </td>
                    <td>
                      <div className="table-actions">
                        {canManageSeatAppointments && item.status === 'Confirmed' && (
                          <button
                            className="btn btn-success btn-compact"
                            onClick={() => handleStaffConfirmAppointment(item)}
                            disabled={confirmingAppointmentId === item.guid}
                          >
                            {confirmingAppointmentId === item.guid ? 'Onaylanıyor...' : 'Onayla'}
                          </button>
                        )}
                        <button
                          className="btn btn-danger btn-compact"
                          onClick={() => handleDelete(item)}
                        >
                          Sil
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="pagination">
          <button
            className="btn btn-light"
            disabled={pagination.pageNumber <= 1}
            onClick={() => loadAppointments({ page: pagination.pageNumber - 1 })}
          >
            Önceki
          </button>
          <span>
            Sayfa {pagination.pageNumber} / {totalPages || 1}
          </span>
          <button
            className="btn btn-light"
            disabled={pagination.pageNumber >= totalPages}
            onClick={() => loadAppointments({ page: pagination.pageNumber + 1 })}
          >
            Sonraki
          </button>
        </div>
      </section>

      {isAdmin && (
        <section className="settings-section">
          <h3>Randevu Ayarları</h3>
          {settings ? (
            <form className="settings-form" onSubmit={handleSaveSettings}>
              <div className="field">
                <label>Günlük Masa Kapasitesi</label>
                <input
                  type="number"
                  min="1"
                  max="500"
                  value={settings.maxSeatsPerDay}
                  onChange={(e) =>
                    setSettings((prev) => ({ ...prev, maxSeatsPerDay: e.target.value }))
                  }
                  required
                />
              </div>
              <div className="field">
                <label>Yeniden Randevu (gün)</label>
                <input
                  type="number"
                  min="1"
                  max="60"
                  value={settings.minDaysBetweenBookings}
                  onChange={(e) =>
                    setSettings((prev) => ({
                      ...prev,
                      minDaysBetweenBookings: e.target.value,
                    }))
                  }
                  required
                />
              </div>
              <div className="field">
                <label>Doğrulama Süresi (dakika)</label>
                <input
                  type="number"
                  min="5"
                  max="180"
                  value={settings.verificationCodeExpiryMinutes}
                  onChange={(e) =>
                    setSettings((prev) => ({
                      ...prev,
                      verificationCodeExpiryMinutes: e.target.value,
                    }))
                  }
                  required
                />
              </div>
              <div className="field">
                <label>Mesai Başlangıcı</label>
                <TimePicker
                  value={settings.workingDayStartTime ? dayjs(settings.workingDayStartTime, 'HH:mm') : null}
                  format="HH:mm"
                  minuteStep={15}
                  showNow={false}
                  allowClear={false}
                  onChange={(time, timeString) =>
                    setSettings((prev) => ({
                      ...prev,
                      workingDayStartTime: timeString,
                    }))
                  }
                  style={{ width: '100%', height: '40px', borderRadius: '8px' }}
                />
              </div>
              <div className="field">
                <label>Mesai Bitişi</label>
                <TimePicker
                  value={settings.workingDayEndTime ? dayjs(settings.workingDayEndTime, 'HH:mm') : null}
                  format="HH:mm"
                  minuteStep={15}
                  showNow={false}
                  allowClear={false}
                  onChange={(time, timeString) =>
                    setSettings((prev) => ({
                      ...prev,
                      workingDayEndTime: timeString,
                    }))
                  }
                  style={{ width: '100%', height: '40px', borderRadius: '8px' }}
                />
              </div>
              <div className="field">
                <label>Randevu Süresi (dakika)</label>
                <input
                  type="number"
                  min="30"
                  max="480"
                  step="15"
                  value={settings.slotDurationMinutes}
                  onChange={(e) =>
                    setSettings((prev) => ({
                      ...prev,
                      slotDurationMinutes: e.target.value,
                    }))
                  }
                  required
                />
              </div>
              <div className="field">
                <label>QR Mode</label>
                <div className="switch-container">
                  <label className="switch">
                    <input
                      type="checkbox"
                      checked={settings.qrModeEnabled}
                      onChange={(e) =>
                        setSettings((prev) => ({
                          ...prev,
                          qrModeEnabled: e.target.checked,
                        }))
                      }
                    />
                    <span className="slider"></span>
                  </label>
                  <span className="switch-label">
                    {settings.qrModeEnabled ? 'Açık' : 'Kapalı'}
                  </span>
                </div>
              </div>
              <button className="btn btn-primary btn-compact" type="submit" disabled={settingsLoading}>
                {settingsLoading ? 'Kaydediliyor…' : 'Ayarları Kaydet'}
              </button>
              <button
                type="button"
                className="btn btn-secondary btn-compact"
                onClick={() => navigate('/library-appointments/blocked-slots')}
                style={{ marginLeft: '1rem' }}
              >
                Kapalı Randevu Aralıkları
              </button>
            </form>
          ) : (
            <p>Ayarlar yükleniyor…</p>
          )}
        </section>
      )}
      {seatModal}
    </div>
  );
};

export default LibraryAppointments;






